import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fmtN, fmtCents, fmtRelative, cn } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';

type Row = {
  id: string;
  userId: string;
  planId: string;
  status: string;
  activatedAt: string | null;
  expiresAt: string | null;
  dataUsedMb: number | null;
  createdAt: string;
  userEmail: string | null;
  planName: string | null;
  planPrice: number | null;
};
type Env = { rows: Row[]; page: number; pageSize: number; total: number };

export function EsimPurchases() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery<Env>({
    queryKey: ['/admin/api/esim/purchases', page],
    queryFn: () => api.get<Env>(`/admin/api/esim/purchases?page=${page}&pageSize=50`),
  });

  return (
    <div>
      <PageHeader
        title="eSIM Purchases"
        subtitle={data ? `${fmtN(data.total)} total` : 'Loading…'}
      />

      <div className="ad-card overflow-hidden">
        <table className="ad-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Plan</th>
              <th>Price</th>
              <th>Status</th>
              <th>Data used</th>
              <th>Activated</th>
              <th>Expires</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center text-text3 py-10">
                  Loading…
                </td>
              </tr>
            ) : (
              data?.rows.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono text-xs">{r.userEmail}</td>
                  <td>{r.planName}</td>
                  <td>{fmtCents(r.planPrice)}</td>
                  <td>
                    <span
                      className={cn(
                        'ad-badge',
                        r.status === 'active'
                          ? 'ad-badge-success'
                          : r.status === 'pending'
                            ? 'ad-badge-warning'
                            : 'ad-badge-neutral',
                      )}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="text-text2">{r.dataUsedMb ? `${(r.dataUsedMb / 1024).toFixed(2)} GB` : '—'}</td>
                  <td className="text-text2">{fmtRelative(r.activatedAt)}</td>
                  <td className="text-text2">{fmtRelative(r.expiresAt)}</td>
                  <td className="text-text2">{fmtRelative(r.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
    </div>
  );
}
